import {
  RATE_TABLE_NAMES,
  type Assignment,
  type RateTableName,
  type Resource,
  type Task,
} from '@bigint-project/core';

export interface TaskAssignmentPanelProps {
  readonly task: Task | null;
  readonly selectionCount: number;
  readonly resources: readonly Resource[];
  readonly assignments: readonly Assignment[];
  readonly onAssign: (resourceId: Resource['id']) => void;
  readonly onUnassign: (assignmentId: Assignment['id']) => void;
  /** 切換此指派所採用的資源成本費率表；費率本身由 resource dialog 維護。 */
  readonly onRateTableChange: (assignmentId: Assignment['id'], table: RateTableName) => void;
  /** 開啟指定資源的費率表設定，不在 panel 內直接編輯 Resource。 */
  readonly onOpenRateTables: (resourceId: Resource['id']) => void;
}

/**
 * 顯示目前選取任務的資源指派。
 *
 * 這是純展示元件：新增與移除都交給呼叫端既有的 store command，避免在 UI 層
 * 重複實作 assignment 的領域規則。
 */
export function TaskAssignmentPanel({
  task,
  selectionCount,
  resources,
  assignments,
  onAssign,
  onUnassign,
  onRateTableChange,
  onOpenRateTables,
}: TaskAssignmentPanelProps): React.JSX.Element {
  if (selectionCount === 0) {
    return <PanelMessage message="選取一項非摘要任務以指派資源。" />;
  }

  if (selectionCount > 1) {
    return <PanelMessage message="一次只能編輯一項任務的資源指派。" />;
  }

  if (task === null || task.isSummary) {
    return <PanelMessage message="摘要任務不能直接指派資源；請選取其中一項工作任務。" />;
  }

  if (resources.length === 0) {
    return (
      <section aria-label="資源指派" className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
        <h2 className="font-medium text-slate-800 dark:text-slate-100">資源指派</h2>
        <p className="mt-1 text-slate-600 dark:text-slate-300">尚無可指派資源。請先在「資源工作表」新增資源。</p>
      </section>
    );
  }

  const resourceById = new Map(resources.map((resource) => [resource.id, resource]));
  const assignedResourceIds = new Set(assignments.map((assignment) => assignment.resourceId));
  const availableResources = resources.filter(
    (resource) => !resource.isBudget && !assignedResourceIds.has(resource.id),
  );

  return (
    <section aria-label="資源指派" className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-medium text-slate-800 dark:text-slate-100">資源指派</h2>
        <span className="truncate text-xs text-slate-500 dark:text-slate-400" title={task.name}>{task.name}</span>
      </div>

      {assignments.length === 0 ? (
        <p className="mt-1 text-slate-600 dark:text-slate-300">此任務尚未指派資源。</p>
      ) : (
        <ul aria-label="目前已指派資源" className="mt-1 flex flex-wrap gap-1.5">
          {assignments.map((assignment) => {
            const resource = resourceById.get(assignment.resourceId);
            const resourceName = resource?.name ?? String(assignment.resourceId);
            return (
              <li key={assignment.id} className="flex items-center gap-1 rounded bg-white px-2 py-1 shadow-sm dark:bg-slate-800">
                <span>{resourceName}</span>
                <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
                  <span className="sr-only">{resourceName} 的費率表</span>
                  <select
                    aria-label={`${resourceName} 的費率表`}
                    value={assignment.rateTable ?? 'A'}
                    onChange={(event) => onRateTableChange(assignment.id, event.target.value as RateTableName)}
                    className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-900"
                  >
                    {RATE_TABLE_NAMES.map((table) => (
                      <option key={table} value={table}>費率表 {table}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  aria-label={`開啟 ${resourceName} 的費率設定`}
                  onClick={() => onOpenRateTables(assignment.resourceId)}
                  className="text-slate-500 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:text-slate-300 dark:hover:text-blue-300"
                >
                  費率設定
                </button>
                <button
                  type="button"
                  aria-label={`移除 ${resourceName} 的資源指派`}
                  onClick={() => onUnassign(assignment.id)}
                  className="text-slate-500 hover:text-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:text-slate-300 dark:hover:text-red-300"
                >
                  移除
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {availableResources.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">所有可指派的一般資源都已指派給此任務。</p>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="可指派資源">
          <span className="text-xs text-slate-500 dark:text-slate-400">可指派：</span>
          {availableResources.map((resource) => (
            <button
              key={resource.id}
              type="button"
              aria-label={`指派 ${resource.name} 到 ${task.name}`}
              onClick={() => onAssign(resource.id)}
              className="rounded border border-blue-300 bg-white px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-blue-700 dark:bg-slate-800 dark:text-blue-300 dark:hover:bg-slate-700"
            >
              + {resource.name}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function PanelMessage({ message }: { readonly message: string }): React.JSX.Element {
  return (
    <section aria-label="資源指派" className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
      <h2 className="font-medium text-slate-800 dark:text-slate-100">資源指派</h2>
      <p className="mt-1">{message}</p>
    </section>
  );
}
